<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import {
  UiButton,
  UiDialog,
  UiIcon,
  UiInputField,
  UiNumberField,
  UiSelect,
  UiSwitch,
  UiText,
  useToaster,
} from "@shopexperts/shared";

import {
  createBonusSignalPreset,
  deleteBonusSignalPreset,
  listBonusSignalPresets,
  listConfidenceSignalGroups,
  updateBonusSignalPreset,
  type BonusSignalPreset,
  type ConfidenceSignalScope,
} from "@/app/services/confidenceSignalsApi.service";

const toaster = useToaster();
const queryClient = useQueryClient();

const presetsQueryKey = ["admin", "bonus-signal-presets"];

const search = ref("");
const groupFilter = ref<string>("");
const scopeFilter = ref<string>("");
const savingKey = ref<string | null>(null);
const deleteTarget = ref<BonusSignalPreset | null>(null);
const isDeleting = ref(false);
const isCreateOpen = ref(false);
const isCreating = ref(false);

const drafts = ref<BonusSignalPreset[]>([]);
const savedSnapshots = ref(new Map<string, string>());

const scopeOptions = [
  { label: "All experts", value: "all" },
  { label: "Agencies", value: "agency" },
  { label: "Freelancers", value: "freelancer" },
];

const scopeFilterOptions = [{ label: "Any scope", value: "" }, ...scopeOptions];

const createForm = reactive({
  group_key: "",
  name: "",
  public_text: "",
  points: "0",
  scope: "all" as ConfidenceSignalScope,
  enabled: true,
});

const { data: groupsResult } = useQuery({
  queryKey: ["admin", "confidence-signal-groups"],
  queryFn: listConfidenceSignalGroups,
  staleTime: 60_000,
});

const {
  data: presets,
  isLoading,
  error,
  refetch,
} = useQuery({
  queryKey: presetsQueryKey,
  queryFn: () => listBonusSignalPresets(),
  staleTime: 30_000,
});

const groups = computed(() => groupsResult.value?.groups ?? []);

const groupOptions = computed(() =>
  [...groups.value]
    .sort((a, b) => a.sort_index - b.sort_index || a.key.localeCompare(b.key))
    .map((group) => ({ label: group.label, value: group.key })),
);

const groupFilterOptions = computed(() => [
  { label: "All groups", value: "" },
  ...groupOptions.value,
]);

// sort_index is intentionally absent: this panel has no control for it, so it
// is server-owned and must never make a row look dirty or be echoed back.
function snapshot(preset: BonusSignalPreset) {
  return JSON.stringify({
    group_key: preset.group_key,
    name: preset.name.trim(),
    public_text: preset.public_text.trim(),
    points: Number(preset.points),
    scope: preset.scope,
    status: preset.status,
  });
}

watch(
  presets,
  (next) => {
    if (!next) return;

    // Preserve rows the admin is still editing: create and delete both
    // invalidate this query, so a refetch can land while another row is dirty.
    const dirty = new Map(
      drafts.value.filter((preset) => isDirty(preset)).map((preset) => [preset.key, preset]),
    );

    const merged = next.map((preset) => ({ ...preset }));
    // Snapshots track server state, so a preserved edit stays dirty.
    savedSnapshots.value = new Map(merged.map((preset) => [preset.key, snapshot(preset)]));

    drafts.value = merged.map((preset) => {
      const edited = dirty.get(preset.key);
      // experts_count and sort_index are server-owned and not editable here;
      // keep them fresh even on a dirty row, since the delete dialog decides
      // cascade from experts_count.
      return edited
        ? { ...edited, experts_count: preset.experts_count, sort_index: preset.sort_index }
        : preset;
    });
  },
  { immediate: true },
);

const filteredPresets = computed(() => {
  const query = search.value.trim().toLowerCase();

  return drafts.value.filter((preset) => {
    if (groupFilter.value && preset.group_key !== groupFilter.value) return false;
    if (scopeFilter.value && preset.scope !== scopeFilter.value) return false;
    if (!query) return true;
    return [preset.key, preset.name, preset.public_text].some((value) =>
      value.toLowerCase().includes(query),
    );
  });
});

const summary = computed(() => ({
  total: drafts.value.length,
  enabled: drafts.value.filter((preset) => preset.status === "enabled").length,
  assigned: drafts.value.reduce((total, preset) => total + preset.experts_count, 0),
}));

function isDirty(preset: BonusSignalPreset) {
  return savedSnapshots.value.get(preset.key) !== snapshot(preset);
}

function setEnabled(preset: BonusSignalPreset, enabled: boolean) {
  preset.status = enabled ? "enabled" : "disabled";
}

function toScope(value: string | string[] | undefined): ConfidenceSignalScope | null {
  return value === "all" || value === "agency" || value === "freelancer" ? value : null;
}

function setScope(preset: BonusSignalPreset, value: string | string[] | undefined) {
  const scope = toScope(value);
  if (scope) preset.scope = scope;
}

function setGroup(preset: BonusSignalPreset, value: string | string[] | undefined) {
  if (typeof value === "string" && value) {
    preset.group_key = value;
  }
}

async function invalidate() {
  await queryClient.invalidateQueries({ queryKey: presetsQueryKey });
}

async function savePreset(preset: BonusSignalPreset) {
  if (!preset.name.trim() || !preset.public_text.trim()) {
    toaster.create({ title: "Name and public text are required", type: "error" });
    return;
  }

  savingKey.value = preset.key;
  try {
    const saved = await updateBonusSignalPreset(preset.key, {
      group_key: preset.group_key,
      name: preset.name.trim(),
      public_text: preset.public_text.trim(),
      points: Number(preset.points),
      scope: preset.scope,
      status: preset.status,
    });
    Object.assign(preset, saved);
    savedSnapshots.value.set(preset.key, snapshot(preset));
    toaster.create({ title: `${preset.name} updated`, type: "success" });
  } catch (saveError) {
    // Narrowing scope while experts of the excluded type hold the preset is
    // rejected by the API; the interceptor surfaces that message.
    console.error("[BonusSignals] Failed to update preset", saveError);
  } finally {
    savingKey.value = null;
  }
}

function openCreate() {
  createForm.group_key = groupOptions.value[0]?.value ?? "";
  createForm.name = "";
  createForm.public_text = "";
  createForm.points = "0";
  createForm.scope = "all";
  createForm.enabled = true;
  isCreateOpen.value = true;
}

async function submitCreate() {
  if (!createForm.group_key) {
    toaster.create({ title: "Pick a group for this bonus signal", type: "error" });
    return;
  }

  if (!createForm.name.trim() || !createForm.public_text.trim()) {
    toaster.create({ title: "Name and public text are required", type: "error" });
    return;
  }

  isCreating.value = true;
  try {
    // `key` is omitted on purpose: the API derives it from the name and
    // guarantees it does not collide with a confidence signal key.
    const created = await createBonusSignalPreset({
      group_key: createForm.group_key,
      name: createForm.name.trim(),
      public_text: createForm.public_text.trim(),
      points: Number(createForm.points),
      scope: createForm.scope,
      status: createForm.enabled ? "enabled" : "disabled",
    });
    await invalidate();
    isCreateOpen.value = false;
    toaster.create({ title: `${created.name} created`, type: "success" });
  } catch (createError) {
    console.error("[BonusSignals] Failed to create preset", createError);
  } finally {
    isCreating.value = false;
  }
}

async function confirmDelete() {
  const preset = deleteTarget.value;
  if (!preset) return;

  isDeleting.value = true;
  try {
    // Cascade only when experts actually hold it, so an accidental delete of an
    // unused preset can never silently strip points from anyone.
    await deleteBonusSignalPreset(preset.key, preset.experts_count > 0);
    await invalidate();
    toaster.create({ title: `${preset.name} deleted`, type: "success" });
    deleteTarget.value = null;
  } catch (deleteError) {
    console.error("[BonusSignals] Failed to delete preset", deleteError);
  } finally {
    isDeleting.value = false;
  }
}

const deleteDialogText = computed(() => {
  const preset = deleteTarget.value;
  if (!preset) return "";
  if (preset.experts_count === 0) {
    return `${preset.name} is not assigned to any expert and will be deleted.`;
  }
  return `${preset.name} is assigned to ${preset.experts_count} expert${preset.experts_count === 1 ? "" : "s"}. Deleting it also removes it from them, lowering their scores.`;
});
</script>

<template>
  <div class="flex flex-col gap-5">
    <div class="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
      <UiText as="p" variant="p-small" class="max-w-2xl text-se-gray-600">
        Presets are reusable bonus definitions an admin attaches to individual experts. They are
        never calculated or auto-assigned, and never raise a group's available-point ceiling — a
        bonus only scores when it fits under the ceiling the definitions already set.
      </UiText>
      <UiButton variant="primary" label="New bonus signal" @click="openCreate">
        <template #before_label><UiIcon name="plus" class="h-4 w-4" /></template>
      </UiButton>
    </div>

    <section class="flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white sm:flex-row sm:items-stretch">
      <div class="flex flex-1 items-center gap-3 px-4 py-3">
        <span class="flex h-9 w-9 items-center justify-center rounded-md bg-amber-50 text-amber-700">
          <UiIcon name="star" class="h-5 w-5" />
        </span>
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.12em] text-gray-400">Preset library</p>
          <p class="mt-0.5 text-sm text-gray-700">
            <strong class="font-semibold text-gray-950">{{ summary.total }}</strong> presets,
            <strong class="font-semibold text-gray-950">{{ summary.enabled }}</strong> enabled
          </p>
        </div>
      </div>
      <div class="border-t border-gray-200 px-5 py-3 sm:border-l sm:border-t-0">
        <p class="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Expert assignments</p>
        <p class="mt-0.5 text-xl font-semibold tabular-nums text-gray-950">{{ summary.assigned }}</p>
      </div>
    </section>

    <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_14rem_12rem]">
      <div>
        <label for="bonus-signal-search" class="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
          Find a preset
        </label>
        <UiInputField
          v-model="search"
          icon="search"
          name="bonus-signal-search"
          placeholder="Search name, copy, or key"
        />
      </div>
      <div>
        <label for="bonus-signal-group-filter" class="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Group</label>
        <UiSelect
          :model-value="groupFilter"
          name="bonus-signal-group-filter"
          :options="groupFilterOptions"
          @update:model-value="(value) => { if (typeof value === 'string') groupFilter = value }"
        />
      </div>
      <div>
        <label for="bonus-signal-scope-filter" class="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Scope</label>
        <UiSelect
          :model-value="scopeFilter"
          name="bonus-signal-scope-filter"
          :options="scopeFilterOptions"
          @update:model-value="(value) => { if (typeof value === 'string') scopeFilter = value }"
        />
      </div>
    </div>

    <div v-if="isLoading" class="rounded-lg border border-gray-100 bg-white py-20 text-center">
      <div class="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-emerald-600" />
      <UiText as="p" variant="p-small" class="text-gray-500">Loading bonus signals...</UiText>
    </div>

    <div v-else-if="error" class="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
      <UiText as="p" variant="p" class="font-medium text-red-700">Bonus signals could not be loaded.</UiText>
      <UiButton class="mt-4" variant="outline" label="Try again" @click="refetch()" />
    </div>

    <div
      v-else-if="filteredPresets.length === 0"
      class="rounded-lg border border-dashed border-gray-300 bg-white py-16 text-center"
    >
      <UiIcon name="star" class="mx-auto mb-3 h-7 w-7 text-gray-300" />
      <UiText as="p" variant="p" class="text-gray-500">
        {{ drafts.length === 0 ? 'No bonus signals yet. Create one to credit experts for something the fixed list does not cover.' : 'No bonus signals match your filters.' }}
      </UiText>
    </div>

    <section v-else class="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div class="hidden grid-cols-[minmax(9rem,0.7fr)_minmax(11rem,0.9fr)_minmax(14rem,1.2fr)_5.5rem_9rem_8rem_6rem_7rem] gap-3 border-b border-gray-100 bg-gray-50/80 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-400 lg:grid">
        <span>Status / key</span>
        <span>Internal name</span>
        <span>Public text</span>
        <span>Points</span>
        <span>Group</span>
        <span>Scope</span>
        <span class="text-center">Experts</span>
        <span class="text-right">Actions</span>
      </div>

      <div class="divide-y divide-gray-100">
        <article
          v-for="preset in filteredPresets"
          :key="preset.key"
          class="grid gap-3 px-4 py-3 transition-colors lg:grid-cols-[minmax(9rem,0.7fr)_minmax(11rem,0.9fr)_minmax(14rem,1.2fr)_5.5rem_9rem_8rem_6rem_7rem] lg:items-center"
          :class="preset.status === 'disabled' ? 'bg-gray-50/70' : 'bg-white hover:bg-gray-50/40'"
        >
          <div class="flex min-w-0 items-center gap-2.5">
            <UiSwitch
              :model-value="preset.status === 'enabled'"
              size="sm"
              :name="`${preset.key}-enabled`"
              @update:model-value="setEnabled(preset, $event)"
            />
            <span class="truncate font-mono text-[11px] text-gray-600" :title="preset.key">{{ preset.key }}</span>
          </div>

          <div>
            <label :for="`${preset.key}-name`" class="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400 lg:hidden">Internal name</label>
            <UiInputField v-model="preset.name" :name="`${preset.key}-name`" />
          </div>

          <div>
            <label :for="`${preset.key}-public-text`" class="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400 lg:hidden">Public text</label>
            <UiInputField v-model="preset.public_text" :name="`${preset.key}-public-text`" />
          </div>

          <div>
            <label :for="`${preset.key}-points`" class="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400 lg:hidden">Points</label>
            <UiNumberField
              :model-value="String(preset.points)"
              :name="`${preset.key}-points`"
              :min="0"
              @update:model-value="(value) => { preset.points = Number(value ?? 0) }"
            />
          </div>

          <div>
            <label :for="`${preset.key}-group`" class="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400 lg:hidden">Group</label>
            <UiSelect
              :model-value="preset.group_key"
              :name="`${preset.key}-group`"
              :options="groupOptions"
              @update:model-value="setGroup(preset, $event)"
            />
          </div>

          <div>
            <label :for="`${preset.key}-scope`" class="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400 lg:hidden">Scope</label>
            <UiSelect
              :model-value="preset.scope"
              :name="`${preset.key}-scope`"
              :options="scopeOptions"
              @update:model-value="setScope(preset, $event)"
            />
          </div>

          <div class="lg:text-center">
            <span class="text-[10px] font-semibold uppercase tracking-wide text-gray-400 lg:hidden">Experts </span>
            <span class="text-sm font-semibold tabular-nums text-gray-800">{{ preset.experts_count }}</span>
          </div>

          <div class="flex items-center justify-end gap-1">
            <UiButton
              variant="primary"
              class="flex-1"
              :label="savingKey === preset.key ? 'Saving...' : 'Save'"
              :disabled="!isDirty(preset) || savingKey === preset.key"
              @click="savePreset(preset)"
            />
            <UiButton
              variant="ghost"
              shape="square"
              :title="`Delete ${preset.name}`"
              :aria-label="`Delete ${preset.name}`"
              :disabled="savingKey === preset.key"
              @click="deleteTarget = preset"
            >
              <template #before_label><UiIcon name="delete" class="h-4 w-4 text-red-600" /></template>
            </UiButton>
          </div>
        </article>
      </div>
    </section>

    <UiDialog
      v-model="isCreateOpen"
      title="New bonus signal"
      text="Creates a reusable preset. It earns nothing until you attach it to an expert."
      primary-action-label="Create bonus signal"
      secondary-action-label="Cancel"
      size="lg"
      :loading="isCreating"
      @primary-action="submitCreate"
      @secondary-action="isCreateOpen = false"
    >
      <div class="flex flex-col gap-4 text-left">
        <div>
          <label for="new-bonus-signal-group" class="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Group</label>
          <UiSelect
            :model-value="createForm.group_key"
            name="new-bonus-signal-group"
            :options="groupOptions"
            @update:model-value="(value) => { if (typeof value === 'string') createForm.group_key = value }"
          />
        </div>

        <div>
          <label for="new-bonus-signal-name" class="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Internal name</label>
          <UiInputField
            v-model="createForm.name"
            name="new-bonus-signal-name"
            placeholder="Shopify Plus Partner"
          />
          <p class="mt-1 text-[11px] text-gray-400">The key is derived from this name and cannot be changed later.</p>
        </div>

        <div>
          <label for="new-bonus-signal-public-text" class="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Public text</label>
          <UiInputField
            v-model="createForm.public_text"
            name="new-bonus-signal-public-text"
            placeholder="Shopify Plus Partner"
          />
        </div>

        <div class="grid gap-4 sm:grid-cols-2">
          <div>
            <label for="new-bonus-signal-points" class="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Points</label>
            <UiNumberField
              :model-value="createForm.points"
              name="new-bonus-signal-points"
              :min="0"
              @update:model-value="(value) => { createForm.points = value ?? '0' }"
            />
          </div>
          <div>
            <label for="new-bonus-signal-scope" class="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Scope</label>
            <UiSelect
              :model-value="createForm.scope"
              name="new-bonus-signal-scope"
              :options="scopeOptions"
              @update:model-value="(value) => { const scope = toScope(value); if (scope) createForm.scope = scope }"
            />
          </div>
        </div>

        <div class="flex items-center gap-3 rounded-lg border border-gray-200 px-4 py-3">
          <UiSwitch v-model="createForm.enabled" size="sm" name="new-bonus-signal-enabled" />
          <div>
            <p class="text-sm font-medium text-gray-900">Enabled</p>
            <p class="text-[11px] text-gray-500">Disabled presets stay attached to experts but earn nothing.</p>
          </div>
        </div>
      </div>
    </UiDialog>

    <UiDialog
      :model-value="Boolean(deleteTarget)"
      title="Delete bonus signal?"
      :text="deleteDialogText"
      primary-action-label="Delete"
      secondary-action-label="Cancel"
      size="sm"
      :loading="isDeleting"
      @update:model-value="(open) => { if (!open) deleteTarget = null }"
      @primary-action="confirmDelete"
      @secondary-action="deleteTarget = null"
    />
  </div>
</template>
